<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type { ArchiveDomain, VersionSummary } from "../types";
import { buildVersionBadges, displayVersionLabel } from "../domain-presentation";
import { versionFamily } from "../version-grouping";

const props = withDefaults(defineProps<{ versions: VersionSummary[]; modelValue: string; domain: ArchiveDomain | null; labelOverride?: string; showAvailability?: boolean }>(), {
  showAvailability: true,
});
const emit = defineEmits<{ select: [version: string] }>();
const root = ref<HTMLElement | null>(null);
const open = ref(false);
const collapsed = ref(new Set<string>());

const family = (version: string) => {
  return versionFamily(version, props.domain?.adapter, props.domain?.game_id);
};
const groups = computed(() => {
  const output = new Map<string, VersionSummary[]>();
  for (const item of props.versions) {
    const key = family(item.version);
    if (!output.has(key)) output.set(key, []);
    output.get(key)!.push(item);
  }
  return [...output.entries()];
});
const displayVersion = (item: VersionSummary) => displayVersionLabel(item.version, item.attributes);
// Multi-channel Android versions (1.2.0@mihoyo / 1.2.0@mihoyo_8) collapse
// into a single picker row showing the plain version number; the first
// channel identity is selected and the artifact cards show the channel.
const pickerRows = computed(() => {
  const output: Array<{ name: string; rows: Array<{ base: string; item: VersionSummary; channelCount: number; states: Record<string, number> }> }> = [];
  for (const [name, items] of groups.value) {
    const seen = new Map<string, { item: VersionSummary; channels: Set<string>; states: Record<string, number> }>();
    for (const item of items) {
      const base = displayVersion(item);
      const attrs = item.attributes || {};
      const channels = new Set<string>();
      if (typeof attrs.channel === "string" && attrs.channel) channels.add(attrs.channel);
      if (typeof attrs.channel_ids === "string") {
        for (const channel of attrs.channel_ids.split("/").map((part) => part.trim()).filter(Boolean)) channels.add(channel);
      }
      const states = {
        available: Number(item.availability_states?.available || 0),
        unavailable: Number(item.availability_states?.unavailable || 0),
        unknown: Number(item.availability_states?.unknown || 0),
      };
      const entry = seen.get(base);
      if (!entry) {
        seen.set(base, { item, channels, states });
      } else {
        for (const channel of channels) entry.channels.add(channel);
        entry.states.available += states.available;
        entry.states.unavailable += states.unavailable;
        entry.states.unknown += states.unknown;
      }
    }
    output.push({
      name,
      rows: [...seen.entries()].map(([base, entry]) => ({
        base,
        item: entry.item,
        channelCount: entry.channels.size || Number(entry.item.artifact_kinds?.apk?.count || 0),
        states: entry.states,
      })),
    });
  }
  return output;
});
const selectedLabel = computed(() => props.labelOverride || displayVersion(props.versions.find((item) => item.version === props.modelValue) || ({ version: props.modelValue, attributes: {} } as VersionSummary)));

function formatFileDate(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return `${year}/${month}/${day}`;
  }
  const normalized = /(?:z|[+-]\d\d:\d\d)$/i.test(value) ? value : `${value}Z`;
  return new Intl.DateTimeFormat("zh-CN", { dateStyle: "short", timeStyle: "short", timeZone: "Asia/Shanghai" }).format(new Date(normalized));
}
function groupSuffix(): string { return "版本"; }
function caps(row: { base: string; item: VersionSummary; channelCount: number; states: Record<string, number> }): Array<{ label: string; tone: string }> {
  // The collapsed row shows the unique channel count and the aggregated
  // availability across all channel identities of the same base version.
  if (props.domain?.adapter === "android" && row.channelCount > 0) {
    const merged = {
      ...row.item,
      artifact_count: row.channelCount,
      artifact_kinds: {
        ...row.item.artifact_kinds,
        apk: { count: row.channelCount, size: row.item.artifact_kinds?.apk?.size || 0 },
      },
      availability_states: row.states,
    } as VersionSummary;
    return buildVersionBadges(props.domain, merged, formatFileDate, props.showAvailability);
  }
  return buildVersionBadges(props.domain, row.item, formatFileDate, props.showAvailability);
}
function toggleFamily(value: string): void {
  const next = new Set(collapsed.value);
  next.has(value) ? next.delete(value) : next.add(value);
  collapsed.value = next;
}
function choose(value: string): void { open.value = false; emit("select", value); }
function onDocumentClick(event: MouseEvent): void { if (!root.value?.contains(event.target as Node)) open.value = false; }
function onKey(event: KeyboardEvent): void { if (event.key === "Escape") open.value = false; }
onMounted(() => { document.addEventListener("click", onDocumentClick); document.addEventListener("keydown", onKey); });
onBeforeUnmount(() => { document.removeEventListener("click", onDocumentClick); document.removeEventListener("keydown", onKey); });
</script>

<template>
  <div ref="root" class="version-picker">
    <span class="version-picker-label">版本选择</span>
    <button class="select-button" type="button" :aria-expanded="open" @click.stop="open = !open"><span>{{ selectedLabel || '—' }}</span><span class="chevron">⌄</span></button>
    <div v-if="open" class="version-menu">
      <div v-for="group in pickerRows" :key="group.name" class="version-group" :class="{ collapsed: collapsed.has(group.name) }">
        <button class="version-group-head" type="button" :aria-expanded="!collapsed.has(group.name)" @click.stop="toggleFamily(group.name)"><span class="group-title"><span class="group-chevron"></span><strong>{{ group.name }} {{ groupSuffix() }}</strong></span><span class="group-meta">{{ group.rows.length }} 个可用版本</span></button>
        <div v-if="!collapsed.has(group.name)" class="version-group-body">
          <button v-for="row in group.rows" :key="row.item.version" class="version-row" :class="{ selected: row.item.version === modelValue }" type="button" @click="choose(row.item.version)"><span class="version-number">{{ row.base }}</span><span class="caps"><span v-for="cap in caps(row)" :key="`${row.item.version}:${cap.label}`" class="cap" :class="cap.tone">{{ cap.label }}</span></span></button>
        </div>
      </div>
    </div>
  </div>
</template>
